import { useId, useLayoutEffect, useRef, type ReactNode } from 'react';
import { Sheet } from '../../ui/Sheet';
import { uiText } from '../../i18n';
import { StudioIcon } from './StudioIcon';

/** Лист экрана создания в оболочке шаблона (BotCore.sheet): фон недоступен, фокус возвращается; бизнес-действий здесь нет. */
export function StudioSheet({
  title,
  children,
  onClose,
  id,
  closeLabel = 'Закрыть',
  footer,
  closeButton = true,
  legacyShell = false,
  open = true,
  backdrop,
}: {
  title: string;
  children: ReactNode;
  onClose(): void;
  id?: string;
  closeLabel?: string;
  footer?: ReactNode;
  closeButton?: boolean;
  /** Тело листа шаблона (.cbody) без своих отступов нативных форм — лист шаблона «Студии» (#stPre).
      Оболочка у всех листов одна, как у шаблона: .bscrim + aside.side.bsheet с классом open (въезд и уход — переходы bot-core.css). */
  legacyShell?: boolean;
  /** лист остаётся в документе закрытым (как #stPre шаблона): уход — переход на том же узле, без копии */
  open?: boolean;
  /** Слой под листом — прямой ребёнок листа перед ручкой и крестиком, вне тела .cbody (аура .pre-au: preHTML шаблона ставит её
      первой в #stPre). В теле её срезали бы маска края (--mt/--mb) и верх тела */
  backdrop?: ReactNode;
}) {
  const generatedId = useId();
  const body = useRef<HTMLDivElement>(null);
  /* каждое открытие — с начала тела, как у шаблона (openPre пишет тело листа заново): лист #stPre один на «Студию» и иначе
     помнил бы прокрутку прошлого шаблона. Стоит до сверки маски края ниже — та читает уже сброшенную прокрутку */
  useLayoutEffect(() => {
    if (open && body.current) body.current.scrollTop = 0;
  }, [open]);
  useLayoutEffect(() => {
    const element = body.current;
    if (!element) return;
    const sync = () => {
      element.style.setProperty('--mt', element.scrollTop > 4 ? '32px' : '0px');
      element.style.setProperty('--mb', element.scrollHeight - element.scrollTop - element.clientHeight > 4 ? '40px' : '0px');
    };
    sync();
    element.addEventListener('scroll', sync, { passive: true });
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(sync);
    observer?.observe(element);
    if (element.firstElementChild) observer?.observe(element.firstElementChild);
    return () => {
      element.removeEventListener('scroll', sync);
      observer?.disconnect();
    };
  }, [children]);
  return (
    <Sheet
      id={id ?? `studio-sheet-${generatedId}`}
      label={uiText(title)}
      open={open}
      onClose={onClose}
      className={legacyShell ? 'st-pre' : `studio-native-sheet${id === 'stPre' ? ' st-pre' : ''}`}
    >
      {backdrop}
      <div className="handle" aria-hidden="true" />
      {closeButton && (
        <button type="button" className="iconb xbtn pre-x" data-bsheet-x aria-label={uiText(closeLabel)} onClick={onClose}>
          <StudioIcon kind="close" />
        </button>
      )}
      <div ref={body} className={legacyShell ? 'cbody' : 'cbody studio-native-sheet-body'}>
        {children}
      </div>
      {footer && <div className="pre-cta">{footer}</div>}
    </Sheet>
  );
}
